import { validate as isUuid } from 'uuid';

/**
 * Returns a hash code for a string.
 * (Compatible to Java's String.hashCode())
 *
 * The hash code for a string object is computed as
 *     s[0]*31^(n-1) + s[1]*31^(n-2) + ... + s[n-1]
 * using number arithmetic, where s[i] is the i th character
 * of the given string, n is the length of the string,
 * and ^ indicates exponentiation.
 * (The hash value of the empty string is zero.)
 * found here: https://gist.github.com/hyamamoto/fd435505d29ebfa3d9716fd2be8d42f0
 *
 * this function is used in builder/base.state.ts and ui-components/hash.ts
 *
 * @param {string} s a string
 * @return {string} a hash code value for the given string as string.
 * The hash is a number but we need it as string in order to avoid confusion with indices of arrays
 */
export const hashCode = (s: string): string => {
  let h = 0,
    i = 0;
  const l = s.length;
  if (l > 0) while (i < l) h = ((h << 5) - h + s.charCodeAt(i++)) | 0;
  return h.toString();
};

/** generate a random alphanumeric string
 * found idea here: https://dev.to/oyetoket/fastest-way-to-generate-random-strings-in-javascript-2k5a
 *
 * @param {number} length - default is 8 (defined in DEFAULT_RANDOM_STRING_LENGTH)
 * @return {string} - random alphanumeric string in defined length
 */
export const DEFAULT_RANDOM_STRING_LENGTH = 8;
export const generateRandomString = (
  length: number = DEFAULT_RANDOM_STRING_LENGTH
): string => {
  return Math.random()
    .toString(36)
    .substring(2, 2 + length);
};

/**
 * check whether the text has a UUID or a 8 character string before a potential '.' as separator.
 * Split parameter by '.' and checks whether first element indicates a translation key by
 * checking for isUUid or string has length 8
 *
 * @param {string} element - string to be checked
 * @return {boolean} true = is translation key, false = there is no translation key
 */
export const isTranslated = (element: string): boolean => {
  const split = element.split('.');
  if (split.length === 1) {
    return false;
  }
  return isUuid(split[0]) || split[0].length === DEFAULT_RANDOM_STRING_LENGTH;
};
